import { useCallback, useEffect, useRef, useState } from 'react';
import type { PanelDesign } from '@/core/types';
import type { MeshStats } from '@/core/mesh/panelMesh';
import { buildExport } from '@/core/export';
import type {
  ExportWorkerRequest,
  ExportWorkerResponse,
  SerializableExportRequest,
} from '@/workers/exportWorker';

/**
 * Runs an export, off the main thread when the browser allows it.
 *
 * If the worker cannot be created — an old browser, a strict content policy —
 * the same code runs inline. The export is the one thing that must never fail
 * for an environmental reason, so it degrades to a stutter rather than an error.
 */

export interface ExportProgress {
  running: boolean;
  completed: number;
  total: number;
  error: string | null;
  lastResult: { filename: string; stats: MeshStats } | null;
}

const IDLE: ExportProgress = {
  running: false,
  completed: 0,
  total: 0,
  error: null,
  lastResult: null,
};

export function downloadBlob(data: Uint8Array | string, filename: string, mimeType: string): void {
  // Blob accepts any ArrayBufferView at runtime; the cast only satisfies the
  // narrower ArrayBuffer-backed type TypeScript infers for typed arrays.
  const url = URL.createObjectURL(new Blob([data as BlobPart], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function useExport(): {
  progress: ExportProgress;
  run(design: PanelDesign, request: SerializableExportRequest): void;
  dismissError(): void;
} {
  const [progress, setProgress] = useState<ExportProgress>(IDLE);
  const workerRef = useRef<Worker | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    try {
      workerRef.current = new Worker(new URL('../../workers/exportWorker.ts', import.meta.url), {
        type: 'module',
      });
    } catch {
      workerRef.current = null;
    }

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  const run = useCallback((design: PanelDesign, request: SerializableExportRequest): void => {
    const worker = workerRef.current;
    const id = ++requestId.current;

    setProgress({ running: true, completed: 0, total: 0, error: null, lastResult: null });

    if (!worker) {
      try {
        const result = buildExport(design, request);
        downloadBlob(result.data, result.filename, result.mimeType);
        setProgress({
          running: false,
          completed: 1,
          total: 1,
          error: null,
          lastResult: { filename: result.filename, stats: result.stats },
        });
      } catch (error) {
        setProgress({
          ...IDLE,
          error: error instanceof Error ? error.message : String(error),
        });
      }
      return;
    }

    const handle = (event: MessageEvent<ExportWorkerResponse>): void => {
      const message = event.data;
      // Ignore replies from a run the user has already superseded.
      if (message.id !== requestId.current) return;

      if (message.type === 'progress') {
        setProgress((current) => ({
          ...current,
          completed: message.completed,
          total: message.total,
        }));
        return;
      }

      worker.removeEventListener('message', handle);

      if (message.type === 'error') {
        setProgress({ ...IDLE, error: message.message });
        return;
      }

      downloadBlob(message.data, message.filename, message.mimeType);
      setProgress({
        running: false,
        completed: 1,
        total: 1,
        error: null,
        lastResult: { filename: message.filename, stats: message.stats },
      });
    };

    worker.addEventListener('message', handle);
    const payload: ExportWorkerRequest = { id, design, request };
    worker.postMessage(payload);
  }, []);

  return {
    progress,
    run,
    dismissError: () => setProgress((current) => ({ ...current, error: null })),
  };
}
